<template>
  <v-layout>
    <v-store-user-info/>
    <h1>
      <span id="subscriptions-title">
        {{ $t('productOverview.productsForRegions') }} {{ regionNames }}
      </span>
      <region-selector-trigger :selected-regions="selectedRegions" @click="regionSelectionModalVisible=true" />
    </h1>
    <div class="notification tip prijswijzer">
      <i class="icon-light-bulb"/>
      <div class="inner">
        <h3 v-html="$t('generic.tip')"/>
        <p v-html="$t('productOverview.travelPlannerTip')"/>
        <p v-if="showSpecialSaleTextZuidHolland"
           id="notification-block-south-holland">
          <br> <b>{{ $t('productOverview.specialSaleTipIntro') }}</b><br>
          <i18n path="productOverview.specialSaleTipContent" tag="span">
            <router-link :to="specialSaleZuidHollandRoute" place="specialSaleLink"> {{
            $t('productOverview.specialSaleTipLinkText') }}
            </router-link>
          </i18n>
        </p>
      </div>
    </div>
    <product-filter-bar>
      <product-filter
        id="product-filter-customer-group"
        :placeholder="$t('productOverview.selectProductGroups')"
        :group-name="'customerGroupFilter'"
        :entities="sortedCustomerGroups"
        :entity-selection="selectedCustomerGroups"
        :multiple="false"
        @entitySelectionChanged="setCustomerGroupSelection"/>
      <product-filter
        id="product-filter-product-collection"
        :placeholder="$t('productOverview.selectProductCollections')"
        :entities="sortedProductCollections"
        :entity-selection="selectedProductCollections"
        @entitySelectionChanged="setProductCollectionSelection"
      />
      <product-filter
        id="product-filter-modality"
        :placeholder="$t('productOverview.selectModalities')"
        :entities="sortedModalities"
        :entity-selection="selectedModalities"
        @entitySelectionChanged="setModalitySelection"
      />
    </product-filter-bar>
    <product-overview-component @revealProductDetail="onRevealProductDetail"/>
    <region-selection-modal v-if="regionSelectionModalVisible"
                            @regionSelectionChanged="regionSelectionChanged"
                            @close="regionSelectionModalVisible=false"/>

    <product-detail-modal v-if="detailsProduct && detailsCustomerGroup"
                          @close="resetConfiguration"/>
  </v-layout>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex'
import VLayout from '../layouts/Default'
import VStoreUserInfo from '../components/user/StoreUserInfo'
import RegionSelectionModal from '../components/modals/RegionSelectorWebshop'
import ProductDetailModal from '../components/modals/ProductDetail'
import ProductFilterBar from '../components/ProductFilterBar'
import ProductFilter from '../components/ProductFilter'
import ProductOverviewComponent from '../components/ProductOverview'
import { applyCriteriaFromUrlParameters } from '../deeplink'
import bodyClassMixin from '../mixins/body-class'
import {
  SET_SELECTED_PRODUCT,
  SET_SELECTED_CUSTOMER_GROUP,
  SET_SELECTED_TERM,
  RESET
} from '../store/modules/configuration/mutation-types'
import RegionSelectorTrigger from '../components/RegionSelectorTrigger'
import { NalShop } from '../store/modules/shop/types'

export default {
  name: 'ProductOverviewPage',
  components: {
    RegionSelectorTrigger,
    VLayout,
    VStoreUserInfo,
    RegionSelectionModal,
    ProductDetailModal,
    ProductFilterBar,
    ProductFilter,
    ProductOverviewComponent
  },
  mixins: [
    bodyClassMixin
  ],
  data () {
    return {
      regionSelectionModalVisible: false,
      preSelection: null
    }
  },
  computed: {
    ...mapState({
      selectedRegions: state => state.selection.selectedFilterEntities.regions,
      selectedCustomerGroups: state => state.selection.selectedFilterEntities.customerGroups,
      selectedProductCollections: state => state.selection.selectedFilterEntities.productCollections,
      selectedModalities: state => state.selection.selectedFilterEntities.modalities,
      detailsProduct: state => state.configuration.selectedProduct
    }),
    ...mapGetters('selection', [
      'sortedCustomerGroups',
      'sortedProductCollections',
      'sortedModalities',
      'availableSelectedRegions'
    ]),
    ...mapGetters('shop', [
      'shopFilterEntityIds'
    ]),
    detailsCustomerGroup () {
      return this.$store.getters['configuration/customerGroupToUseForConfiguration']
    },
    regionSelectionModalTriggerTitle () {
      return this.selectedRegions.length === 0
        ? this.$t('productOverview.selectRegion')
        : this.$t('productOverview.changeRegionSelection')
    },
    showSpecialSaleTextZuidHolland () {
      const regionId = this.$container.config.productApiRegionZuidHollandId
      return !!this.selectedRegions.find(region => {
        return region.id === regionId
      })
    },
    regionNames () {
      const names = this.availableSelectedRegions
        .map(region => region.title)
        .sort((a, b) => a.localeCompare(b))
      const nameString = [names.slice(0, -1).join(', '), names.slice(-1)[0]]
        .join(names.length < 2 ? '' : ` ${this.$t('generic.and')} `)

      return nameString.length > 0 ? this.$t('generic.for') + ' ' + nameString : nameString
    },
    specialSaleZuidHollandRoute: function () {
      return {
        name: 'SpecialSale',
        params: {
          lang: this.$router.currentRoute.params.lang,
          sale: 'special-sale',
          slug: 'zuid-holland2017'
        }
      }
    }
  },
  watch: {
    // @todo: remove watch on store property
    selectedRegions: function () {
      this.$store.dispatch('selection/fetchFilters')
      this.$store.dispatch('selection/fetchProductConfigurations')
    }
  },
  mounted: async function () {
    await this.$store.dispatch('shop/switchShop', new NalShop())
    await this.$store.dispatch('shop/fetchBasicFilters')
    await this.$store.dispatch('selection/fetchFilters')
    await this.$store.dispatch('selection/fetchRegions')
    applyCriteriaFromUrlParameters(this.$route.query)
    this.$store.dispatch('selection/fetchProductConfigurations')
  },
  methods: {
    ...mapActions('selection', [
      'setCustomerGroupSelection',
      'setProductCollectionSelection',
      'setModalitySelection',
      'unselectFilterEntity',
      'unselectFilterEntities'
    ]),
    onRevealProductDetail: function (product, customerGroup, term = null) {
      this.$store.commit('configuration/' + SET_SELECTED_PRODUCT, product)
      this.$store.commit('configuration/' + SET_SELECTED_CUSTOMER_GROUP, customerGroup)
      this.$store.commit('configuration/' + SET_SELECTED_TERM, term)
    },
    resetConfiguration: function () {
      this.$store.commit('configuration/' + RESET)
    },
    regionSelectionChanged: function () {
      this.$store.dispatch('selection/fetchFilters')
      this.$store.dispatch('selection/fetchProductConfigurations')
    },
    clearFilterEntitySelection: async function () {
      await this.unselectFilterEntities(this.entities)
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/pages/ProductOverview.vue